import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "@/App.css";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ChatWidget from "@/components/ChatWidget";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";

import HomePage from "@/pages/HomePage";

const ProductsPage = lazy(() => import("@/pages/ProductsPage"));
const ProductDetailPage = lazy(() => import("@/pages/ProductDetailPage"));
const CartCheckoutPage = lazy(() => import("@/pages/CartCheckoutPage"));
const AccessoriesPage = lazy(() => import("@/pages/AccessoriesPage"));
const VideosPage = lazy(() => import("@/pages/VideosPage"));
const ProductionPage = lazy(() => import("@/pages/ProductionPage"));
const ReviewsPage = lazy(() => import("@/pages/ReviewsPage"));
const PaymentCallbackPage = lazy(() => import("@/pages/PaymentCallbackPage"));
const DistributorPage = lazy(() => import("@/pages/DistributorPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const DeliveryPolicyPage = lazy(() => import("@/pages/DeliveryPolicyPage"));
const RefundPolicyPage = lazy(() => import("@/pages/RefundPolicyPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const WarrantyPage = lazy(() => import("@/pages/WarrantyPage"));
const AdminLoginPage = lazy(() => import("@/pages/AdminLoginPage"));
const AdminDashboardPage = lazy(() => import("@/pages/AdminDashboardPage"));

const Fallback = () => (
  <div className="py-32 text-center text-sm" style={{ color: "var(--ink-soft)" }} aria-busy="true">
    Loading…
  </div>
);

function PublicLayout({ children }) {
  return (
    <>
      <a href="#main" className="skip-link">Skip to content</a>
      <Navbar />
      <main id="main">{children}</main>
      <Footer />
      <ChatWidget />
    </>
  );
}

function Shell() {
  const loc = useLocation();
  const isAdmin = loc.pathname.startsWith("/admin");
  if (isAdmin) {
    return (
      <ErrorBoundary resetKey={loc.pathname}>
        <Suspense fallback={<Fallback />}>
          <Routes>
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin" element={<AdminDashboardPage />} />
            <Route path="/admin/*" element={<AdminDashboardPage />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    );
  }
  return (
    <PublicLayout>
      <ErrorBoundary resetKey={loc.pathname}>
      <Suspense fallback={<Fallback />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/products/:sku" element={<ProductDetailPage />} />
          <Route path="/accessories" element={<AccessoriesPage />} />
          <Route path="/videos" element={<VideosPage />} />
          <Route path="/production" element={<ProductionPage />} />
          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/cart" element={<CartCheckoutPage />} />
          <Route path="/payment/callback" element={<PaymentCallbackPage />} />
          <Route path="/order/success" element={<PaymentCallbackPage />} />
          <Route path="/distributors" element={<DistributorPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/refund-policy" element={<RefundPolicyPage />} />
          <Route path="/warranty" element={<WarrantyPage />} />
          <Route path="/delivery-policy" element={<DeliveryPolicyPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="*" element={<HomePage />} />
        </Routes>
      </Suspense>
      </ErrorBoundary>
    </PublicLayout>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <Shell />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
